import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../components/ui/dropdownMenu";
import type { StipulationActionReasonView, StipulationActionView } from "./api/types";

interface StipulationActionMenuProperties {
  actions: StipulationActionView[];
  /** Text shown in the trigger (the current action, or the "Select One" placeholder). */
  currentLabel?: string;
  disabled?: boolean;
  onSelect: (action: StipulationActionView, reason?: StipulationActionReasonView) => void;
}

// Action dropdown for a stipulation row. Actions with `reasons` render a nested
// submenu (e.g. Pre-Approval Granted → proof types); flat actions apply directly.
// The menu only emits the selection — the row decides whether to prompt for a
// note (see `requiresNote`) before applying.
function StipulationActionMenu({ actions, currentLabel, disabled, onSelect }: StipulationActionMenuProperties) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        className="border-border text-foreground focus-visible:ring-ring flex min-w-36 items-center justify-between gap-2 rounded border bg-transparent px-2 py-0.5 text-xs focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
      >
        <span>{currentLabel ?? t("stipulations.selectOne")}</span>
        <ChevronDown aria-hidden="true" className="text-muted-foreground size-3 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        {actions.map((action) =>
          action.reasons.length > 0 ? (
            <DropdownMenuSub key={action.actionCode}>
              <DropdownMenuSubTrigger className="py-1 text-xs">{action.displayName}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {action.reasons.map((reason) => (
                  <DropdownMenuItem
                    key={`${action.actionCode}:${reason.reasonPk ?? reason.displayName}`}
                    className="py-1 text-xs"
                    onSelect={() => onSelect(action, reason)}
                  >
                    {reason.displayName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem key={action.actionCode} className="py-1 text-xs" onSelect={() => onSelect(action)}>
              {action.displayName}
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default StipulationActionMenu;
